import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ProfileLeftSidebar from "./ProfileLeftSidebar";
import { AuthContext } from "../../helpers/AuthContext";
import { canUseEzeeAI } from "../../utils/ezeeAccess";

function formatReset(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function UsageCard({ title, usage, icon }) {
  const used = Number(usage?.used || 0);
  const limit = Math.max(Number(usage?.limit || 0), 1);
  const percentage = Math.min((used / limit) * 100, 100);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={800}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ mt: 0.25 }}>
              {used} <Typography component="span" variant="body1" color="text.secondary">/ {usage?.limit || 0}</Typography>
            </Typography>
          </Box>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: "#fff1f1",
              color: "#c70909",
            }}
          >
            {icon}
          </Box>
        </Stack>

        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            mt: 2,
            height: 8,
            borderRadius: 999,
            bgcolor: "#eef1f4",
            "& .MuiLinearProgress-bar": { bgcolor: "#c70909", borderRadius: 999 },
          }}
        />

        <Stack direction="row" justifyContent="space-between" spacing={2} mt={1.25}>
          <Typography variant="caption" color="text.secondary">
            {usage?.remaining ?? 0} remaining
          </Typography>
          <Typography variant="caption" color="text.secondary" textAlign="right">
            Resets {formatReset(usage?.resetAt)}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ProfileEzeeAI() {
  const { id } = useParams();
  const { authState } = useContext(AuthContext);
  const [usage, setUsage] = useState(null);
  const [targetUser, setTargetUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isOwnProfile = Number(authState?.id) === Number(id);
  const isAdmin = ["administrator", "super-administrator"].includes(
    String(authState?.role || "").toLowerCase().replace(/[_\s]+/g, "-")
  );
  const canView = (isOwnProfile && canUseEzeeAI(authState)) || isAdmin;

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      setError("You do not have access to this Ezee AI usage profile.");
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    axios
      .get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/profile/${id}/usage`, {
        headers: { accessToken: localStorage.getItem("accessToken") },
      })
      .then((response) => {
        if (cancelled) return;
        setUsage(response.data?.usage || null);
        setTargetUser(response.data?.user || null);
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(
          requestError?.response?.data?.message ||
            "Unable to load Ezee AI usage right now."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canView, id]);

  const usageOwnerLabel = useMemo(() => {
    const name = [targetUser?.first_name, targetUser?.last_name].filter(Boolean).join(" ").trim();
    if (name) return `${name}${name.endsWith("s") ? "'" : "'s"}`;
    return isOwnProfile ? "Your" : "User";
  }, [isOwnProfile, targetUser]);

  return (
    <div className="ec-app-instances">
      <ProfileLeftSidebar />
      <div className="rightSide">
        <div className="options-container fade-in-effect-usability">
          <div className="options-inner-container">
            <Box
              sx={{
                border: "1px solid #e4e8ec",
                borderRadius: 3,
                bgcolor: "#fff",
                p: { xs: 2, md: 3 },
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={2}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2.5,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "#fff1f1",
                      color: "#c70909",
                    }}
                  >
                    <AutoAwesomeIcon />
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight={800}>
                      Ezee AI Usage
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {usageOwnerLabel} Ezee AI allowance and activity.
                    </Typography>
                  </Box>
                </Stack>

                <Chip
                  label="Usage limits"
                  icon={<QueryStatsIcon />}
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
              </Stack>

              {loading ? (
                <Box sx={{ py: 5 }}>
                  <LinearProgress />
                </Box>
              ) : null}

              {error ? <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert> : null}

              {!loading && !error && usage ? (
                <>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                      gap: 2,
                      mt: 3,
                    }}
                  >
                    <UsageCard
                      title="This hour"
                      usage={usage.hourly}
                      icon={<ScheduleIcon />}
                    />
                    <UsageCard
                      title="This week"
                      usage={usage.weekly}
                      icon={<QueryStatsIcon />}
                    />
                  </Box>

                  <Card variant="outlined" sx={{ mt: 2, borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="overline" color="text.secondary" fontWeight={800}>
                        Lifetime Ezee AI activity
                      </Typography>
                      <Typography variant="h3" fontWeight={800} sx={{ mt: 0.5 }}>
                        {Number(usage.lifetimeQuestions || 0).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Questions submitted to Ezee AI
                      </Typography>

                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mt={2} useFlexGap flexWrap="wrap">
                        {usage.lastUsedAt ? (
                          <Chip
                            size="small"
                            label={`Last used ${new Date(usage.lastUsedAt).toLocaleString()}`}
                          />
                        ) : (
                          <Chip size="small" label="No Ezee AI questions yet" />
                        )}
                        {usage.lastInstanceUniqueId ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Last instance: ${usage.lastInstanceUniqueId}`}
                          />
                        ) : null}
                      </Stack>
                    </CardContent>
                  </Card>

                  <Alert severity="info" sx={{ mt: 2 }}>
                    Ezee AI usage is stored persistently, so server restarts do not reset your
                    hourly or weekly allowance.
                  </Alert>
                </>
              ) : null}
            </Box>
          </div>
        </div>
      </div>
    </div>
  );
}
